import React, { useState, useContext, useEffect } from 'react';
import { CreditCard, Coins, Check, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import AppContext from '../../context/AppContext';
import { transformAggregators } from '../../utils/aggregatorUtils';
const PaymentSelector = () => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedMethodKey, setSelectedMethodKey] = useState(null);
  const [error, setError] = useState(null);
  const amount = 5000; // Montant en FCFA pour le plan Premium
  const navigate = useNavigate();

  const { 
    allAggregators = [], 
    userActiveAggregators = [],
    method, setMethod,
    adminActiveAggregators, setAdminActiveAggregators
  } = useContext(AppContext);
  
  // Logs pour comprendre les données disponibles
  useEffect(() => {
    console.log("PaymentSelector - Données chargées:", {
      allAggregators: allAggregators ? (Array.isArray(allAggregators) ? `Array(${allAggregators.length})` : typeof allAggregators) : "undefined",
      userActiveAggregators: userActiveAggregators ? (Array.isArray(userActiveAggregators) ? `Array(${userActiveAggregators.length})` : typeof userActiveAggregators) : "undefined",
      adminActiveAggregators: adminActiveAggregators ? (Array.isArray(adminActiveAggregators) ? `Array(${adminActiveAggregators.length})` : typeof adminActiveAggregators) : "undefined"
    });
    
    if (allAggregators && typeof allAggregators === 'object') {
      console.log("Détail des agrégateurs disponibles:", allAggregators);
    }
  }, [allAggregators, userActiveAggregators, adminActiveAggregators]);
/*
  const paymentMethods = [
    {
      id: 'monero',
      name: 'Monero (XMR)',
      icon: <Coins className="w-5 h-5" />,
      countries: ['Bénin', 'Burkina Faso', 'Côte d\'Ivoire', 'Mali', 'Niger']
    },
    {
      id: 'card',
      name: 'Carte Bancaire',
      icon: <CreditCard className="w-5 h-5" />,
      countries: ['Sénégal', 'Togo', 'Cameroun', 'Congo', 'Gabon']
    }
  ];
*/
const getIconPath = (key) => {
  switch (key) {
    case 'monero':
      return "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v4.7c0 4.83-3.4 9.36-7 10.46-3.6-1.1-7-5.63-7-10.46v-4.7l7-3.12z"; // Chemin pour une icône de monnaie
    case 'card':
      return "M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4V6h16v12zm-6-7h-4v3h4v-3z"; // Chemin pour une icône de carte
    default:
      return "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"; // Icône par défaut
  }
};


// Transformer les agrégateurs en format utilisable
const transformedAggregators = React.useMemo(() => {
  if (!allAggregators || typeof allAggregators !== 'object') {
    return [];
  }
  return transformAggregators(allAggregators);
}, [allAggregators]);

// Utiliser les agrégateurs transformés
const paymentMethods = (!transformedAggregators || transformedAggregators.length === 0)
  ? []
  : transformedAggregators.map(agg => {
      if (!agg?.name) return null;
      
      let countries = [];
      if (agg.name === 'monero') {
        countries = ['Bénin', 'Burkina Faso', 'Côte d\'Ivoire', 'Mali', 'Niger'];
      } else {
        countries = ['Sénégal', 'Togo', 'Cameroun', 'Congo', 'Gabon'];
      }

      return {
        id: agg.id,
        name: agg.name || 'Unknown Provider',
        key: agg.id,
        icon: (
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d={getIconPath(agg.name)} />
          </svg>
        ),
        countries: countries
      };
    }).filter(Boolean);


  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-sm mx-auto">
        {/* En-tête */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">Paiement Plan Premium</h1>
          <p className="text-center text-gray-600">Choisissez votre méthode de paiement préférée</p>
        </div>
      
        {/* Montant */}
        <div className="bg-white rounded-lg p-4 mb-4 text-center shadow-sm">
          <h2 className="text-2xl font-bold text-gray-800">
            {amount.toLocaleString()} FCFA
          </h2>
          <p className="text-sm text-gray-500">Abonnement mensuel</p>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700">{error}</p>
              <button 
                onClick={() => navigate('/Plans')} 
                className="text-xs text-red-600 mt-2 underline"
              >
                Retour au choix des plans
              </button>
            </div>
          </div>
        )}

        {/* Options de paiement */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <p className="text-sm text-gray-500 mb-4">
            Choisissez votre mode de paiement :
          </p>

          {paymentMethods.length === 0 ? (
            <div className="text-center py-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-700">
                  Aucun agrégateur de paiement n'est disponible actuellement. Veuillez contacter le support.
                </p>
                <button 
                  onClick={() => navigate('/Plans')} 
                  className="mt-3 text-sm text-blue-600 font-medium"
                >
                  Retour aux plans
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`rounded-lg border p-4 ${
                    selectedMethod === method.id
                      ? 'bg-blue-50 border-blue-300'
                      : 'border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => {
                      console.log(`Méthode sélectionnée: ${method.id}, clé: ${method.key}`);
                      setMethod(method.id);
                      setSelectedMethodKey(method.key);
                      setSelectedMethod(method.id);
                    }}
                    className="w-full text-left"
                  >
                    <div className="flex items-center gap-3">
                      {/* Case à cocher */}
                      <div className={`w-5 h-5 border rounded flex items-center justify-center ${
                        selectedMethod === method.id
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedMethod === method.id && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>

                      <div className={`${
                        selectedMethod === method.id ? 'text-blue-500' : 'text-gray-400'
                      }`}>
                        {method.icon}
                      </div>
                      <span className={`font-medium ${
                        selectedMethod === method.id ? 'text-blue-700' : 'text-gray-700'
                      }`}>
                        {method.name}
                      </span>
                    </div>
                  </button>

                  {/* Pays toujours visibles */}
                  <div className="mt-3 ml-8 text-sm text-gray-500">
                    Disponible en : {method.countries.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bouton de validation */}
        <div className="relative mt-4">
          <button
            onClick={() => {
              if (!selectedMethod || !selectedMethodKey) {
                setError("Veuillez sélectionner une méthode de paiement");
                return;
              }
              
              // Si Moneroo est sélectionné, on vérifie qu'on a bien une clé API configrée
              if (selectedMethod === 'monero' && (!selectedMethodKey || !selectedMethodKey.includes('monero'))) {
                setError("Configuration incomplète pour Moneroo");
                return;
              }
              
              console.log(`Navigation vers /IndexPaiement avec: id=${method}, key=${selectedMethodKey}`);
              navigate(`/IndexPaiement?id=${selectedMethod}&key=${selectedMethodKey}`);
            }}
            disabled={!selectedMethod || paymentMethods.length === 0}
            className={`w-full p-3 rounded-lg font-medium transition-all ${
              selectedMethod && paymentMethods.length > 0
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {selectedMethod ? 'Valider le paiement' : 'Sélectionnez un mode de paiement'}
          </button>
          
          <button
            onClick={() => navigate('/Plans')}
            className="w-full mt-3 p-3 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all"
          >
            Retour
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSelector;